import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import ProductCard from "../components/ProductCard";
import { Slider } from "../components/ui/slider";
import { Checkbox } from "../components/ui/checkbox";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "../components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { SlidersHorizontal, X } from "lucide-react";

function FilterGroup({ title, options, selected, onToggle, testid }) {
  return (
    <div className="py-4 border-b border-slate-200 dark:border-slate-800">
      <h4 className="font-display font-bold text-sm mb-3 uppercase tracking-wide">{title}</h4>
      <div className="space-y-2">
        {options.map((opt) => (
          <label key={opt} className="flex items-center gap-2 cursor-pointer text-sm">
            <Checkbox
              checked={selected.includes(opt)}
              onCheckedChange={() => onToggle(opt)}
              data-testid={`${testid}-${opt.toLowerCase().replace(/\s/g, "-")}`}
            />
            <span className="text-slate-600 dark:text-slate-300">{opt}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const { formatPrice, cur } = useApp();
  const [products, setProducts] = useState([]);
  const [facets, setFacets] = useState({ categories: [], genders: [], fits: [], sizes: [] });
  const [loading, setLoading] = useState(true);

  const search = params.get("search") || "";
  const [categories, setCategories] = useState(params.get("category")?.split(",").filter(Boolean) || []);
  const [genders, setGenders] = useState(params.get("gender")?.split(",").filter(Boolean) || []);
  const [fits, setFits] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [sort, setSort] = useState("newest");
  const [priceRange, setPriceRange] = useState([0, 5000]);

  useEffect(() => {
    api.get("/products/meta/facets").then(({ data }) => {
      setFacets(data);
      setPriceRange([data.min_price, data.max_price]);
    });
  }, []);

  // keep in sync with url for category/gender coming from navbar links
  useEffect(() => {
    setCategories(params.get("category")?.split(",").filter(Boolean) || []);
    setGenders(params.get("gender")?.split(",").filter(Boolean) || []);
  }, [params]);

  const fetchProducts = useCallback(() => {
    setLoading(true);
    const qp = new URLSearchParams();
    if (categories.length) qp.set("category", categories.join(","));
    if (genders.length) qp.set("gender", genders.join(","));
    if (fits.length) qp.set("fit", fits.join(","));
    if (sizes.length) qp.set("size", sizes.join(","));
    if (search) qp.set("search", search);
    if (sort) qp.set("sort", sort);
    qp.set("min_price", priceRange[0]);
    qp.set("max_price", priceRange[1]);
    api.get(`/products?${qp.toString()}`).then(({ data }) => {
      setProducts(data);
      setLoading(false);
    });
  }, [categories, genders, fits, sizes, search, sort, priceRange]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const toggle = (setter, arr) => (val) =>
    setter(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

  const clearAll = () => {
    setCategories([]);
    setGenders([]);
    setFits([]);
    setSizes([]);
    setPriceRange([facets.min_price, facets.max_price]);
    setParams({});
  };

  const activeCount = categories.length + genders.length + fits.length + sizes.length;

  const FilterPanel = () => (
    <div data-testid="filter-panel">
      <div className="flex items-center justify-between py-4 border-b">
        <h3 className="font-display font-bold">Filters</h3>
        {activeCount > 0 && (
          <button onClick={clearAll} className="text-xs text-[#E63946] font-semibold" data-testid="clear-filters">
            Clear all ({activeCount})
          </button>
        )}
      </div>
      <FilterGroup title="Category" options={facets.categories} selected={categories} onToggle={toggle(setCategories, categories)} testid="filter-category" />
      <FilterGroup title="Gender" options={facets.genders} selected={genders} onToggle={toggle(setGenders, genders)} testid="filter-gender" />
      <FilterGroup title="Fit" options={facets.fits} selected={fits} onToggle={toggle(setFits, fits)} testid="filter-fit" />
      <FilterGroup title="Size" options={facets.sizes} selected={sizes} onToggle={toggle(setSizes, sizes)} testid="filter-size" />
      <div className="py-4">
        <h4 className="font-display font-bold text-sm mb-3 uppercase tracking-wide">Price (INR)</h4>
        <Slider
          min={facets.min_price || 0}
          max={facets.max_price || 5000}
          step={100}
          value={priceRange}
          onValueChange={setPriceRange}
          data-testid="price-range-slider"
          className="my-4"
        />
        <div className="flex justify-between text-sm font-mono-price text-slate-500">
          <span>₹{priceRange[0]}</span>
          <span>₹{priceRange[1]}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h1 className="font-display text-2xl sm:text-3xl font-bold">
          {search ? `Results for "${search}"` : genders.length ? `${genders.join(" & ")}'s Collection` : categories.length ? categories.join(" & ") : "All Products"}
        </h1>
        <p className="text-sm text-slate-400 mt-1">{products.length} products • Prices in {cur.code}</p>
      </div>

      <div className="flex gap-8">
        {/* sidebar desktop */}
        <aside className="hidden lg:block w-64 shrink-0">
          <FilterPanel />
        </aside>

        <div className="flex-1 min-w-0">
          {/* top bar */}
          <div className="flex items-center justify-between mb-5 gap-3">
            <Sheet>
              <SheetTrigger asChild>
                <button className="lg:hidden flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold" data-testid="mobile-filter-trigger">
                  <SlidersHorizontal size={16} /> Filters {activeCount > 0 && `(${activeCount})`}
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80 overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filters</SheetTitle>
                </SheetHeader>
                <FilterPanel />
              </SheetContent>
            </Sheet>

            {/* active pills */}
            <div className="hidden sm:flex items-center gap-2 flex-wrap flex-1">
              {[...categories, ...genders, ...fits, ...sizes].map((f) => (
                <span key={f} className="inline-flex items-center gap-1 bg-slate-100 dark:bg-slate-800 text-xs font-medium px-2.5 py-1 rounded-full">
                  {f}
                </span>
              ))}
            </div>

            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-44 rounded-full" data-testid="sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="price_asc">Price: Low to High</SelectItem>
                <SelectItem value="price_desc">Price: High to Low</SelectItem>
                <SelectItem value="rating">Top Rated</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-[3/4] rounded-2xl bg-slate-100 dark:bg-slate-800 animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20 text-slate-400">
              <X size={40} className="mx-auto mb-3" />
              No products match your filters.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6" data-testid="product-grid">
              {products.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
